import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Upload, X } from "lucide-react";
import type { PollType, RankingOption } from "./pollTypes";

interface PollRankingEditorProps {
  pollType: Extract<PollType, "ranking" | "ranking_minimum">;
  rankingOptions: RankingOption[];
  minimumRequiredRanks: number;
  setMinimumRequiredRanks: (value: number) => void;
  addRankingOption: () => void;
  moveRankingOption: (from: number, to: number) => void;
  removeRankingOption: (index: number) => void;
  updateRankingOption: (index: number, field: keyof RankingOption, value: string) => void;
  handleRankingImageUpload: (optionIndex: number, file: File) => void;
  handleRankingRemoveImage: (optionIndex: number, imageUrl: string) => void;
}

export const PollRankingEditor = ({
  pollType,
  rankingOptions,
  minimumRequiredRanks,
  setMinimumRequiredRanks,
  addRankingOption,
  moveRankingOption,
  removeRankingOption,
  updateRankingOption,
  handleRankingImageUpload,
  handleRankingRemoveImage,
}: PollRankingEditorProps) => {
  return (
    <>
      <p className="text-sm text-muted-foreground">
        {pollType === "ranking_minimum"
          ? "Readers must rank at least the minimum number of options. Unranked options will be treated as a shared average rank."
          : "Readers will drag to reorder these options from highest to lowest preference."}
      </p>

      {pollType === "ranking_minimum" && (
        <div className="space-y-2">
          <Label htmlFor="minimum-required">Minimum required rankings</Label>
          <Input
            id="minimum-required"
            type="number"
            min={1}
            max={Math.max(1, rankingOptions.length)}
            value={minimumRequiredRanks}
            onChange={(e) => {
              const parsed = parseInt(e.target.value, 10);
              if (Number.isNaN(parsed)) return;
              setMinimumRequiredRanks(Math.min(Math.max(1, parsed), Math.max(1, rankingOptions.length)));
            }}
          />
          <p className="text-xs text-muted-foreground">
            Readers must rank at least {minimumRequiredRanks} option{minimumRequiredRanks === 1 ? "" : "s"}. They can drag
            more items into the ranked list if they wish.
          </p>
        </div>
      )}

      <div className="space-y-3">
        {rankingOptions.map((option, index) => (
          <Card key={index} className="p-3">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="font-medium">Option {index + 1}</p>
                <p className="text-xs text-muted-foreground">Drag handles will appear in the reader view.</p>
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  disabled={index === 0}
                  onClick={() => moveRankingOption(index, index - 1)}
                  aria-label="Move option up"
                >
                  ↑
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  disabled={index === rankingOptions.length - 1}
                  onClick={() => moveRankingOption(index, index + 1)}
                  aria-label="Move option down"
                >
                  ↓
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeRankingOption(index)}
                  aria-label="Remove option"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <Label>Label *</Label>
                <Input
                  value={option.label}
                  onChange={(e) => updateRankingOption(index, "label", e.target.value)}
                  placeholder="e.g., Favorite feature"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={option.description}
                  onChange={(e) => updateRankingOption(index, "description", e.target.value)}
                  placeholder="Optional description"
                  rows={2}
                />
              </div>
              <div>
                <Label>Image</Label>
                {option.image_url ? (
                  <div className="flex items-center gap-2 p-2 border rounded-lg">
                    <img src={option.image_url} alt={option.label} className="w-16 h-16 object-cover rounded" />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRankingRemoveImage(index, option.image_url)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleRankingImageUpload(index, file);
                      }}
                      className="hidden"
                      id={`ranking-image-upload-${index}`}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => document.getElementById(`ranking-image-upload-${index}`)?.click()}
                      className="w-full"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Image
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Button type="button" variant="outline" onClick={addRankingOption} className="w-full">
        <Plus className="w-4 h-4 mr-2" /> Add Option ({rankingOptions.length}/15)
      </Button>
    </>
  );
};
